import { uiSurface } from "shared/ui/uiSurface"
import {
  Business,
  CameraAlt,
  Check,
  Close,
  Edit,
  LocationOn,
} from "@mui/icons-material"
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetProviderDetailQuery,
  useGetProviderLogoQuery,
  useUpdateProviderTimezoneMutation,
  useUploadProviderLogoMutation,
} from "../../apis/appApi"

const InfoRow: FC<{
  label: string
  value: React.ReactNode
  "data-testid"?: string
}> = ({ label, value, "data-testid": testId }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={{ xs: 0.5, sm: 2 }}
    data-testid={testId}
    sx={{ py: 1 }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      fontWeight={500}
      sx={{ minWidth: { sm: 180 } }}
    >
      {label}
    </Typography>
    <Typography variant="body2" color="text.primary" fontWeight={500}>
      {value ?? "\u2014"}
    </Typography>
  </Stack>
)

export const ProviderProfileTab: FC = () => {
  const { t } = useTranslation()
  const { data: provider, isLoading } = useGetProviderDetailQuery()
  const { data: logoData } = useGetProviderLogoQuery()
  const [updateTimezone] = useUpdateProviderTimezoneMutation()
  const [uploadLogo] = useUploadProviderLogoMutation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [editingTimezone, setEditingTimezone] = useState(false)
  const [selectedTimezone, setSelectedTimezone] = useState<string | undefined>(undefined)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const timezoneOptions = useMemo(
    () => Intl.supportedValuesOf("timeZone"),
    [],
  )

  const handleTimezoneEdit = () => {
    setSelectedTimezone(provider?.timezone ?? undefined)
    setEditingTimezone(true)
  }

  const handleTimezoneCancel = () => {
    setEditingTimezone(false)
    setSelectedTimezone(undefined)
  }

  const handleLogoClick = () => {
    fileInputRef.current?.click()
  }

  const handleLogoFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/svg+xml",
    ]
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: t("configuration.provider.logoTypeError"),
        severity: "error",
      })
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: t("configuration.provider.logoSizeError"),
        severity: "error",
      })
      return
    }

    const reader = new FileReader()
    reader.onload = async (evt) => {
      const base64 = (evt.target?.result as string).split(",")[1]
      if (!base64) return
      try {
        await uploadLogo({
          data: base64,
          filename: file.name,
          mimeType: file.type,
        }).unwrap()
        setSnackbar({
          open: true,
          message: t("configuration.provider.logoUpdated"),
          severity: "success",
        })
      } catch {
        setSnackbar({
          open: true,
          message: "Failed to upload logo",
          severity: "error",
        })
      }
    }
    reader.readAsDataURL(file)
    // Reset input so re-selecting the same file triggers onChange
    e.target.value = ""
  }

  const logoSrc = logoData
    ? `data:${logoData.mimeType};base64,${logoData.data}`
    : undefined

  const handleTimezoneSave = async () => {
    if (!selectedTimezone) return
    try {
      await updateTimezone({ timeZone: selectedTimezone }).unwrap()
      setSnackbar({
        open: true,
        message: t("configuration.provider.timezoneUpdated"),
        severity: "success",
      })
      setEditingTimezone(false)
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to update timezone",
        severity: "error",
      })
    }
  }

  if (isLoading) {
    return (
      <Box
        data-testid="provider-loading"
        sx={{ display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const address = provider?.address

  return (
    <Container {...uiSurface("UI-DLV2-175")} data-testid="provider-tab" maxWidth="md" sx={{ py: 4 }}>
      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          borderRadius: 3,
          borderColor: "divider",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: 4,
            bgcolor: "grey.50",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              style={{ display: "none" }}
              onChange={handleLogoFileSelect}
            />
            <Tooltip
              title={
                logoSrc
                  ? t("configuration.provider.changeLogo")
                  : t("configuration.provider.uploadLogo")
              }
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <CameraAlt
                    sx={{
                      fontSize: 18,
                      bgcolor: "primary.main",
                      color: "white",
                      borderRadius: "50%",
                      p: 0.4,
                    }}
                  />
                }
                sx={{ cursor: "pointer" }}
                onClick={handleLogoClick}
              >
                <Avatar
                  src={logoSrc}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: "primary.main",
                    fontSize: "2rem",
                    cursor: "pointer",
                  }}
                >
                  {provider?.name?.charAt(0)?.toUpperCase() || (
                    <Business fontSize="large" />
                  )}
                </Avatar>
              </Badge>
            </Tooltip>
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                {provider?.name ||
                  t("configuration.provider.unknown", "Unknown provider")}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {provider?.code
                  ? `Code: ${provider.code}`
                  : t("configuration.provider.noCode", "No code")}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Provider Info */}
        <Box sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Business color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={600}>
              {t("configuration.provider.info")}
            </Typography>
          </Stack>
          <Box sx={{ pl: 4 }}>
            <InfoRow
              data-testid="provider-detail-name"
              label={t("configuration.provider.name")}
              value={provider?.name}
            />
            <Divider sx={{ my: 1 }} />
            <InfoRow
              data-testid="provider-detail-code"
              label={t("configuration.provider.code")}
              value={provider?.code}
            />
            <Divider sx={{ my: 1 }} />
            <InfoRow
              data-testid="provider-detail-email"
              label={t("configuration.provider.email")}
              value={provider?.email}
            />
            <Divider sx={{ my: 1 }} />
            <InfoRow
              data-testid="provider-detail-phone"
              label={t("configuration.provider.phone")}
              value={provider?.phone}
            />
            <Divider sx={{ my: 1 }} />
            {/* Timezone — editable (F163) */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.5, sm: 2 }}
              data-testid="provider-detail-timezone"
              sx={{ py: 1, alignItems: { sm: "center" } }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ minWidth: { sm: 180 } }}
              >
                {t("configuration.provider.timezone")}
              </Typography>
              {editingTimezone ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <Autocomplete
                    size="small"
                    options={timezoneOptions}
                    value={selectedTimezone}
                    onChange={(_, value) => setSelectedTimezone(value ?? undefined)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("configuration.provider.selectTimezone")}
                        size="small"
                      />
                    )}
                    sx={{ minWidth: 280 }}
                    disableClearable
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleTimezoneSave}
                    disabled={!selectedTimezone}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleTimezoneCancel}>
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.primary" fontWeight={500}>
                    {provider?.timezone ?? "\u2014"}
                  </Typography>
                  <Tooltip title={t("configuration.provider.timezone")}>
                    <IconButton size="small" onClick={handleTimezoneEdit}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>

        <Divider />

        {/* Address */}
        <Box sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <LocationOn color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={600}>
              {t("configuration.provider.address")}
            </Typography>
          </Stack>
          <Box sx={{ pl: 4 }}>
            <InfoRow
              data-testid="provider-detail-street"
              label={t("configuration.provider.street")}
              value={
                address
                  ? [address.streetNumber, address.streetName]
                      .filter(Boolean)
                      .join(" ")
                  : null
              }
            />
            <Divider sx={{ my: 1 }} />
            <InfoRow
              data-testid="provider-detail-city"
              label={t("configuration.provider.city")}
              value={address?.city}
            />
            <Divider sx={{ my: 1 }} />
            <InfoRow
              data-testid="provider-detail-postcode"
              label={t("configuration.provider.postcode")}
              value={address?.postcode}
            />
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
